import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, Suspense, useCallback, useState } from 'react';

import { Button as OutlineButton } from '@/components/shadcn/ui/button';
import { createSignalMutationOptions } from '@/entities/signal';
import { errorHandler } from '@/error-handler';
import usePreventUnsaved from '@/hooks/pure/usePreventUnsaved';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { useAppForm } from '@/shared/ui/form';
import { loadingToast, toast } from '@/stores/pure/ToastStore';

import { createSignalFormOptions } from '../model/schema';
import { SignalFormContent } from './signal-form-content';

// Hook을 사용하기 위한 wrapper component
function BlockRefresh({
  isBlocked,
  children,
}: {
  isBlocked: boolean;
  children: ReactNode;
}) {
  usePreventUnsaved({ notSaved: isBlocked });
  return <>{children}</>;
}

export type CreateSignalDialogProps = {
  isOpen: boolean;
  close: () => void;
  unmount: () => void;
  workspaceId: string;
};

export function CreateSignalDialog({
  isOpen,
  close,
  unmount,
  workspaceId,
}: CreateSignalDialogProps) {
  const queryClient = useQueryClient();
  const { mutateAsync } = useMutation(createSignalMutationOptions(queryClient));
  const [hasValidPreview, setHasValidPreview] = useState(false);

  const handlePreviewComplete = useCallback(() => {
    setHasValidPreview(true);
  }, []);

  const handleFormValueChange = useCallback(() => {
    setHasValidPreview(false);
  }, []);

  const form = useAppForm({
    ...createSignalFormOptions,
    onSubmit: async (value) => {
      const dismissToast = loadingToast(
        'Processing classification',
        'Please wait while we create.',
      );

      try {
        await mutateAsync({
          workspace_id: workspaceId,
          title: value.value.title,
          definition: value.value.definition,
        });

        toast('Signal created', 'success');
      } catch (error) {
        toast(errorHandler(error, 'Failed to create signal'), 'fail');
      } finally {
        dismissToast();
      }

      close();
    },
  });

  return (
    <form.Subscribe selector={(state) => state.isSubmitting}>
      {(isSubmitting) => (
        <BlockRefresh isBlocked={isSubmitting}>
          <Dialog
            open={isOpen}
            onOpenChange={(open) => {
              if (!open && !isSubmitting) close();
            }}
            onExitComplete={unmount}
          >
            <DialogContent size="xl">
              <DialogHeader>
                <DialogTitle>Create signal</DialogTitle>
                <DialogDescription>
                  Describe the issues you want to detect. We'll find matching
                  ones.
                </DialogDescription>
              </DialogHeader>

              <DialogBody className="flex-1 min-h-0">
                <Suspense fallback={<></>}>
                  <SignalFormContent
                    signalId={''}
                    originalTitle={''}
                    form={form}
                    workspaceId={workspaceId}
                    isSubmitting={isSubmitting}
                    onPreviewComplete={handlePreviewComplete}
                    onFormValueChange={handleFormValueChange}
                  />
                </Suspense>
              </DialogBody>

              <DialogFooter>
                <div />
                <div className="flex gap-2">
                  <OutlineButton
                    variant="outline"
                    onClick={close}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </OutlineButton>
                  <Button
                    variant="default"
                    isLoading={isSubmitting}
                    disabled={!hasValidPreview}
                    onClick={async () => {
                      // 클릭 시 모든 필드 유효성 검사 트리거
                      await form.validateAllFields('change');
                      if (!form.state.isFieldsValid) return;
                      await form.handleSubmit();
                    }}
                  >
                    Create
                  </Button>
                </div>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </BlockRefresh>
      )}
    </form.Subscribe>
  );
}
